import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    Modal,
    Pagination,
    ProgressBar,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import Select from "react-select";
import {
    approveHRPayrollPeriod,
    approveHRLeaveRequest,
    createHRAttendance,
    createHRApplicant,
    createHRComplianceDocument,
    createHRDepartment,
    createHREmployee,
    createHRJobOpening,
    createHRLeaveRequest,
    createHRLeaveType,
    createHRPayrollAccountMapping,
    createHRPayrollItem,
    createHRPayrollPeriod,
    createHRPerformanceReview,
    createHRRole,
    createHRSalaryStructure,
    deleteHRDepartment,
    deleteHRRole,
    deleteHRSalaryStructure,
    fetchHRAttendance,
    fetchAccounts,
    fetchHRApplicants,
    fetchHRComplianceDocuments,
    fetchHRDashboard,
    fetchHRDepartments,
    fetchHREmployees,
    fetchHRJobOpenings,
    fetchHRLeaveRequests,
    fetchHRLeaveTypes,
    fetchHRPayrollItems,
    fetchHRPayrollAccountMappings,
    fetchHRPayrollPeriods,
    fetchHRPayrollTransitionReport,
    fetchHRPerformanceReviews,
    fetchHRRoles,
    fetchHRSalaryStructures,
    fetchStores,
    markHRPayrollPeriodPaid,
    postHRPayrollPeriodToAccounting,
    rejectHRLeaveRequest,
    reopenHRPayrollPeriod,
    reverseHRPayrollPeriodPosting,
    updateHRAttendance,
    updateHRApplicant,
    updateHRComplianceDocument,
    updateHRDepartment,
    updateHREmployee,
    updateHRJobOpening,
    updateHRLeaveType,
    updateHRPayrollAccountMapping,
    updateHRPayrollItem,
    updateHRPayrollPeriod,
    updateHRRole,
    updateHRSalaryStructure,
} from "../api/api";
import { useUser } from "../UserContext";

const modules = [
    { key: "employees", label: "Employee Master" },
    { key: "attendance", label: "Attendance" },
    { key: "leave", label: "Leave" },
    { key: "payroll", label: "Payroll" },
    { key: "performance", label: "Performance" },
    { key: "compliance", label: "Compliance" },
    { key: "recruitment", label: "Recruitment" },
    { key: "analytics", label: "Analytics" },
    { key: "settings", label: "Settings" },
];

const blankEmployee = {
    employee_number: "",
    first_name: "",
    last_name: "",
    id_number: "",
    kra_pin: "",
    sha_number: "",
    nssf_number: "",
    phone_number: "",
    email: "",
    employment_type: "FULL_TIME",
    store: "",
    department: "",
    role: "",
    salary_structure: "",
    supervisor: "",
    status: "ACTIVE",
    hire_date: "",
    termination_date: "",
};

const blankSalary = {
    name: "",
    employment_type: "FULL_TIME",
    pay_frequency: "MONTHLY",
    basic_salary: "",
    daily_rate: "",
    housing_allowance: "",
    transport_allowance: "",
    other_allowances: "",
    overtime_hourly_rate: "",
};

const kenyaLeaveTypes = [
    { name: "Annual Leave", annual: "21", statutory: "21", note: "Minimum 21 working days after 12 consecutive months." },
    { name: "Sick Leave", annual: "14", statutory: "14", note: "Common statutory baseline is 7 days full pay plus 7 days half pay." },
    { name: "Maternity Leave", annual: "90", statutory: "90", note: "Three months with full pay." },
    { name: "Paternity Leave", annual: "14", statutory: "14", note: "Two weeks with full pay." },
    { name: "Compassionate Leave", annual: "0", statutory: "0", note: "Policy based, useful for family emergencies." },
    { name: "Unpaid Leave", annual: "0", statutory: "0", note: "Policy based, useful after paid entitlement is exhausted." },
];

const payrollPeriodExamples = [
    "Monthly salaried staff: May 2026, 2026-05-01 to 2026-05-31.",
    "Weekly casuals: Week 19 Casual Payroll, 2026-05-04 to 2026-05-10.",
    "Mid-month advance: May 2026 Salary Advance, 2026-05-01 to 2026-05-15.",
];

const payrollLineExamples = [
    "Full-time cashier: basic pay + housing/transport allowances - PAYE/SHA/NSSF.",
    "Casual warehouse worker: daily rate multiplied by attended days plus overtime.",
    "Late/absence adjustment: penalty in deductions, with attendance record as support.",
    "Overtime: hours from attendance multiplied by salary structure overtime rate.",
];

const PAGE_SIZE = 10;

const currency = (value) => `Ksh. ${Math.round(Number(value || 0)).toLocaleString()}`;
const percent = (value) => Math.min(100, Math.max(0, Number(value || 0)));

const splitApplicantName = (fullName) => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    return {
        first_name: parts.slice(0, 2).join(" "),
        last_name: parts.slice(2).join(" "),
    };
};

const optionLabel = (option, labelKey) => {
    if (!option) return "";
    if (typeof labelKey === "function") return labelKey(option);
    return option[labelKey] || option.name || option.title || option.full_name || "";
};

const cleanNullable = (value) => value || null;

const salaryPayload = (form) => ({
    ...form,
    basic_salary: form.basic_salary || "0",
    daily_rate: form.daily_rate || "0",
    housing_allowance: form.housing_allowance || "0",
    transport_allowance: form.transport_allowance || "0",
    other_allowances: form.other_allowances || "0",
    overtime_hourly_rate: form.overtime_hourly_rate || "0",
});

const reviewScore = (review) => {
    const scores = [
        Number(review.attendance_score || 0),
        Number(review.productivity_score || 0),
        Number(review.quality_score || 0),
        Number(review.initiative_score || 0),
    ];
    return Math.max(0, Math.min(100, scores.reduce((total, score) => total + score, 0) / scores.length));
};

const performanceFlag = (score) => {
    if (score >= 90) return { label: "Promotion eligible", variant: "success" };
    if (score >= 85) return { label: "Salary increment eligible", variant: "primary" };
    if (score < 50) return { label: "Dismissal review", variant: "danger" };
    if (score < 65) return { label: "Warning review", variant: "warning" };
    return { label: "Stable", variant: "secondary" };
};

const HR = () => {
    const { user } = useUser();
    const isAdmin = Boolean(
        user?.is_superuser ||
        user?.groups?.includes("Admin") ||
        user?.groups?.includes("Administrator") ||
        user?.groups?.includes("Superuser") ||
        user?.groups?.includes("HR Admin")
    );

    const [activeModule, setActiveModule] = useState("employees");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [pages, setPages] = useState({});

    const [dashboard, setDashboard] = useState({});
    const [stores, setStores] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [salaryStructures, setSalaryStructures] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [payrollPeriods, setPayrollPeriods] = useState([]);
    const [payrollItems, setPayrollItems] = useState([]);
    const [performanceReviews, setPerformanceReviews] = useState([]);
    const [complianceDocuments, setComplianceDocuments] = useState([]);
    const [jobOpenings, setJobOpenings] = useState([]);
    const [applicants, setApplicants] = useState([]);

    const [employeeForm, setEmployeeForm] = useState(blankEmployee);
    const [employeeEditId, setEmployeeEditId] = useState(null);
    const [employeeFilters, setEmployeeFilters] = useState({ name: "", store: "", department: "", salary_structure: "" });

    const [attendanceSetup, setAttendanceSetup] = useState({ attendance_date: "", store: "", shift_name: "" });
    const [attendanceBatch, setAttendanceBatch] = useState([]);
    const [attendanceEditId, setAttendanceEditId] = useState(null);
    const [attendanceEditForm, setAttendanceEditForm] = useState(null);
    const [absenceModal, setAbsenceModal] = useState(null);

    const [leaveForm, setLeaveForm] = useState({
        employee: "",
        leave_type: "",
        start_date: "",
        end_date: "",
        days_requested: "",
        balance_before: "0",
        balance_after: "0",
        reason: "",
    });
    const [leaveTypeForm, setLeaveTypeForm] = useState({
        name: "",
        annual_entitlement_days: "21",
        statutory_minimum_days: "21",
        is_paid: true,
    });
    const [leaveTypeEditId, setLeaveTypeEditId] = useState(null);

    const blankPayrollPeriod = { name: "", start_date: "", end_date: "", status: "DRAFT", payroll_posting_mode: "STANDARD" };
    const blankPayrollItem = {
        period: "",
        employee: "",
        store: "",
        basic_pay: "0",
        allowances: "0",
        overtime_pay: "0",
        deductions: "0",
        paye: "0",
        sha: "0",
        nssf: "0",
        net_pay: "0",
    };
    const blankPayrollMapping = {
        id: null,
        employee: "",
        store: "",
        department: "",
        legacy_salary_expense_account: "",
        staff_advance_account: "",
        default_payroll_expense_account: "",
        payroll_payable_account: "",
        paye_payable_account: "",
        sha_payable_account: "",
        nssf_payable_account: "",
        is_active: true,
        notes: "",
    };
    const [payrollPeriodForm, setPayrollPeriodForm] = useState(blankPayrollPeriod);
    const [payrollPeriodEditId, setPayrollPeriodEditId] = useState(null);
    const [payrollItemForm, setPayrollItemForm] = useState(blankPayrollItem);
    const [payrollItemEditId, setPayrollItemEditId] = useState(null);
    const [payrollPaymentAccount, setPayrollPaymentAccount] = useState("");
    const [payrollMappings, setPayrollMappings] = useState([]);
    const [payrollMappingForm, setPayrollMappingForm] = useState(blankPayrollMapping);
    const [payrollTransitionReport, setPayrollTransitionReport] = useState({});
    const [accounts, setAccounts] = useState([]);

    const [performanceForm, setPerformanceForm] = useState({
        employee: "",
        period_start: "",
        period_end: "",
        attendance_score: "80",
        late_days: "0",
        productivity_score: "80",
        quality_score: "80",
        initiative_score: "80",
        action: "",
        notes: "",
    });
    const [performanceFilters, setPerformanceFilters] = useState({ employee: "", store: "" });

    const [complianceForm, setComplianceForm] = useState({
        employee: "",
        document_type: "SHA",
        document_number: "",
        expiry_date: "",
        status: "VALID",
        notes: "",
    });
    const [complianceEditId, setComplianceEditId] = useState(null);
    const [complianceFilters, setComplianceFilters] = useState({ employee: "", store: "", document_type: "", status: "" });

    const [jobOpeningForm, setJobOpeningForm] = useState({
        title: "",
        store: "",
        department: "",
        role: "",
        status: "OPEN",
        opened_at: "",
    });
    const [jobOpeningEditId, setJobOpeningEditId] = useState(null);
    const [selectedJobOpening, setSelectedJobOpening] = useState("");
    const [applicantForm, setApplicantForm] = useState({
        job_opening: "",
        full_name: "",
        phone_number: "",
        email: "",
        status: "APPLIED",
        notes: "",
    });

    const [departmentForm, setDepartmentForm] = useState({ id: null, name: "", description: "", is_active: true });
    const [roleForm, setRoleForm] = useState({ id: null, department: "", title: "", description: "", is_active: true });
    const [salaryForm, setSalaryForm] = useState(blankSalary);
    const [salaryEditId, setSalaryEditId] = useState(null);
    const [analyticsStoreFilter, setAnalyticsStoreFilter] = useState("");
    const [storePerformanceTargets, setStorePerformanceTargets] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("hrStorePerformanceTargets") || "{}");
        } catch {
            return {};
        }
    });

    const loadHR = useCallback(async () => {
        setLoading(true);
        try {
            const [
                dashboardData,
                storeData,
                departmentData,
                roleData,
                salaryData,
                employeeData,
                attendanceData,
                leaveTypeData,
                leaveRequestData,
                payrollPeriodData,
                payrollItemData,
                payrollMappingData,
                payrollTransitionData,
                accountData,
                performanceData,
                complianceData,
                jobOpeningData,
                applicantData,
            ] = await Promise.all([
                fetchHRDashboard(),
                fetchStores(),
                fetchHRDepartments(),
                fetchHRRoles(),
                fetchHRSalaryStructures(),
                fetchHREmployees(),
                fetchHRAttendance(),
                fetchHRLeaveTypes(),
                fetchHRLeaveRequests(),
                fetchHRPayrollPeriods(),
                fetchHRPayrollItems(),
                fetchHRPayrollAccountMappings(),
                fetchHRPayrollTransitionReport(),
                fetchAccounts(),
                fetchHRPerformanceReviews(),
                fetchHRComplianceDocuments(),
                fetchHRJobOpenings(),
                fetchHRApplicants(),
            ]);

            setDashboard(dashboardData || {});
            setStores(Array.isArray(storeData) ? storeData : (storeData?.results || []));
            setDepartments(departmentData || []);
            setRoles(roleData || []);
            setSalaryStructures(salaryData || []);
            setEmployees(employeeData || []);
            setAttendance(attendanceData || []);
            setLeaveTypes(leaveTypeData || []);
            setLeaveRequests(leaveRequestData || []);
            setPayrollPeriods(payrollPeriodData || []);
            setPayrollItems(payrollItemData || []);
            setPayrollMappings(payrollMappingData || []);
            setPayrollTransitionReport(payrollTransitionData || {});
            setAccounts(Array.isArray(accountData) ? accountData : (accountData?.results || []));
            setPerformanceReviews(performanceData || []);
            setComplianceDocuments(complianceData || []);
            setJobOpenings(jobOpeningData || []);
            setApplicants(applicantData || []);
        } catch (error) {
            console.error("Failed to load HR data:", error);
            setMessage({ type: "danger", text: "Failed to load HR data." });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadHR();
    }, [loadHR]);

    useEffect(() => {
        localStorage.setItem("hrStorePerformanceTargets", JSON.stringify(storePerformanceTargets));
    }, [storePerformanceTargets]);

    const submit = async (handler, successText) => {
        setSaving(true);
        setMessage(null);
        try {
            await handler();
            await loadHR();
            setMessage({ type: "success", text: successText });
        } catch (error) {
            console.error("HR save failed:", error);
            const data = error.response?.data;
            const detail = data?.detail || data?.non_field_errors?.join(" ") || JSON.stringify(data || {});
            setMessage({ type: "danger", text: detail && detail !== "{}" ? detail : "Could not save HR record." });
        } finally {
            setSaving(false);
        }
    };

    const setPage = (key, page) => {
        setPages((current) => ({ ...current, [key]: page }));
    };

    const getPageData = (key, rows) => {
        const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
        const currentPage = Math.min(pages[key] || 1, totalPages);
        const start = (currentPage - 1) * PAGE_SIZE;
        return {
            currentPage,
            totalPages,
            pageRows: rows.slice(start, start + PAGE_SIZE),
        };
    };

    const renderPagination = (key, totalPages, currentPage) => (
        <div className="d-flex justify-content-center mt-3">
            <Pagination size="sm">
                <Pagination.First onClick={() => setPage(key, 1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setPage(key, Math.max(currentPage - 1, 1))} disabled={currentPage === 1} />
                {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                    const pageNumber = start + index;
                    if (pageNumber > totalPages) return null;
                    return (
                        <Pagination.Item key={pageNumber} active={pageNumber === currentPage} onClick={() => setPage(key, pageNumber)}>
                            {pageNumber}
                        </Pagination.Item>
                    );
                })}
                <Pagination.Next onClick={() => setPage(key, Math.min(currentPage + 1, totalPages))} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setPage(key, totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
        </div>
    );

    const renderNativeSelect = (value, onChange, options, placeholder, labelKey = "name") => (
        <Form.Select value={value || ""} onChange={(event) => onChange(event.target.value)}>
            <option value="">{placeholder}</option>
            {options.map((option) => (
                <option key={option.id} value={option.id}>
                    {optionLabel(option, labelKey)}
                </option>
            ))}
        </Form.Select>
    );

    const renderSearchableSelect = ({ value, onChange, options, placeholder, labelKey = "name" }) => {
        const selectOptions = options.map((option) => ({
            value: option.id,
            label: optionLabel(option, labelKey),
        }));
        return (
            <Select
                isClearable
                options={selectOptions}
                placeholder={placeholder}
                value={selectOptions.find((option) => String(option.value) === String(value)) || null}
                onChange={(option) => onChange(option?.value || "")}
            />
        );
    };

    const selectedPayrollEmployee = useMemo(
        () => employees.find((employee) => String(employee.id) === String(payrollItemForm.employee)),
        [employees, payrollItemForm.employee]
    );

    useEffect(() => {
        if (!selectedPayrollEmployee) return;
        setPayrollItemForm((current) => ({
            ...current,
            store: current.store || selectedPayrollEmployee.store,
        }));
    }, [selectedPayrollEmployee]);

    const filteredEmployees = useMemo(() => {
        const name = employeeFilters.name.trim().toLowerCase();
        return employees.filter((employee) => {
            const nameMatch =
                !name ||
                `${employee.full_name || ""} ${employee.employee_number || ""}`.toLowerCase().includes(name);
            const storeMatch = !employeeFilters.store || String(employee.store) === String(employeeFilters.store);
            const departmentMatch = !employeeFilters.department || String(employee.department) === String(employeeFilters.department);
            const salaryMatch =
                !employeeFilters.salary_structure ||
                String(employee.salary_structure) === String(employeeFilters.salary_structure);
            return nameMatch && storeMatch && departmentMatch && salaryMatch;
        });
    }, [employeeFilters, employees]);

    const startEmployeeEdit = (employee) => {
        setEmployeeEditId(employee.id);
        setEmployeeForm({
            ...blankEmployee,
            ...employee,
            supervisor: employee.supervisor || "",
            termination_date: employee.termination_date || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const saveEmployee = () => submit(async () => {
        const payload = {
            ...employeeForm,
            supervisor: cleanNullable(employeeForm.supervisor),
            termination_date: cleanNullable(employeeForm.termination_date),
        };
        if (employeeEditId) {
            await updateHREmployee(employeeEditId, payload);
        } else {
            await createHREmployee(payload);
        }
        setEmployeeEditId(null);
        setEmployeeForm(blankEmployee);
    }, employeeEditId ? "Employee updated." : "Employee saved.");

    const renderEmployees = () => {
        const { currentPage, totalPages, pageRows } = getPageData("employees", filteredEmployees);
        return (
            <>
                <Card className="mb-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Card.Title className="mb-0">{employeeEditId ? "Edit Employee" : "Add Employee"}</Card.Title>
                            {employeeEditId && (
                                <Button variant="outline-secondary" size="sm" onClick={() => {
                                    setEmployeeEditId(null);
                                    setEmployeeForm(blankEmployee);
                                }}>
                                    Cancel Edit
                                </Button>
                            )}
                        </div>
                        <Row className="g-3">
                            <Col md={3}>
                                <Form.Label>Employee number</Form.Label>
                                <Form.Control placeholder="Auto-generated if blank" value={employeeForm.employee_number} onChange={(e) => setEmployeeForm({ ...employeeForm, employee_number: e.target.value })} />
                            </Col>
                            <Col md={4}>
                                <Form.Label>First name(s)</Form.Label>
                                <Form.Control placeholder="First two names, e.g. Mary Wanjiku" value={employeeForm.first_name} onChange={(e) => setEmployeeForm({ ...employeeForm, first_name: e.target.value })} />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Last name</Form.Label>
                                <Form.Control placeholder="Surname or remaining name" value={employeeForm.last_name} onChange={(e) => setEmployeeForm({ ...employeeForm, last_name: e.target.value })} />
                            </Col>
                            <Col md={2}>
                                <Form.Label>Employment type</Form.Label>
                                <Form.Select value={employeeForm.employment_type} onChange={(e) => setEmployeeForm({ ...employeeForm, employment_type: e.target.value })}>
                                    <option value="FULL_TIME">Full-time</option>
                                    <option value="CASUAL">Casual</option>
                                    <option value="CONTRACT">Contract</option>
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label>ID number</Form.Label>
                                <Form.Control placeholder="National ID" value={employeeForm.id_number} onChange={(e) => setEmployeeForm({ ...employeeForm, id_number: e.target.value })} />
                            </Col>
                            <Col md={3}>
                                <Form.Label>KRA PIN</Form.Label>
                                <Form.Control placeholder="KRA PIN" value={employeeForm.kra_pin} onChange={(e) => setEmployeeForm({ ...employeeForm, kra_pin: e.target.value })} />
                            </Col>
                            <Col md={3}>
                                <Form.Label>SHA number</Form.Label>
                                <Form.Control placeholder="SHA membership number" value={employeeForm.sha_number} onChange={(e) => setEmployeeForm({ ...employeeForm, sha_number: e.target.value })} />
                            </Col>
                            <Col md={3}>
                                <Form.Label>NSSF number</Form.Label>
                                <Form.Control placeholder="NSSF number" value={employeeForm.nssf_number} onChange={(e) => setEmployeeForm({ ...employeeForm, nssf_number: e.target.value })} />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Phone</Form.Label>
                                <Form.Control placeholder="Phone number" value={employeeForm.phone_number} onChange={(e) => setEmployeeForm({ ...employeeForm, phone_number: e.target.value })} />
                            </Col>
                            <Col md={5}>
                                <Form.Label>Email</Form.Label>
                                <Form.Control type="email" placeholder="Email address" value={employeeForm.email} onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })} />
                            </Col>
                            <Col md={2}>
                                <Form.Label>Hire date</Form.Label>
                                <Form.Control type="date" value={employeeForm.hire_date} onChange={(e) => setEmployeeForm({ ...employeeForm, hire_date: e.target.value })} />
                            </Col>
                            <Col md={2}>
                                <Form.Label>Status</Form.Label>
                                <Form.Select value={employeeForm.status} onChange={(e) => setEmployeeForm({ ...employeeForm, status: e.target.value })}>
                                    <option value="ACTIVE">Active</option>
                                    <option value="SUSPENDED">Suspended</option>
                                    <option value="TERMINATED">Terminated</option>
                                    <option value="ON_LEAVE">On Leave</option>
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label>Store</Form.Label>
                                {renderNativeSelect(employeeForm.store, (value) => setEmployeeForm({ ...employeeForm, store: value }), stores, "Select store")}
                            </Col>
                            <Col md={3}>
                                <Form.Label>Department</Form.Label>
                                {renderNativeSelect(employeeForm.department, (value) => setEmployeeForm({ ...employeeForm, department: value }), departments, "Select department")}
                            </Col>
                            <Col md={3}>
                                <Form.Label>Role</Form.Label>
                                {renderNativeSelect(employeeForm.role, (value) => setEmployeeForm({ ...employeeForm, role: value }), roles, "Select role", "title")}
                            </Col>
                            <Col md={3}>
                                <Form.Label>Salary structure</Form.Label>
                                {renderNativeSelect(employeeForm.salary_structure, (value) => setEmployeeForm({ ...employeeForm, salary_structure: value }), salaryStructures, "Select salary structure")}
                            </Col>
                            <Col md={3}>
                                <Form.Label>Supervisor</Form.Label>
                                {renderSearchableSelect({
                                    value: employeeForm.supervisor,
                                    onChange: (value) => setEmployeeForm({ ...employeeForm, supervisor: value }),
                                    options: employees.filter((employee) => employee.id !== employeeEditId),
                                    placeholder: "Search supervisor",
                                    labelKey: "full_name",
                                })}
                            </Col>
                            <Col md={3}>
                                <Form.Label>Termination date</Form.Label>
                                <Form.Control type="date" value={employeeForm.termination_date || ""} onChange={(e) => setEmployeeForm({ ...employeeForm, termination_date: e.target.value })} />
                            </Col>
                            <Col md={3} className="d-flex align-items-end">
                                <Button disabled={saving} onClick={saveEmployee}>{employeeEditId ? "Update Employee" : "Save Employee"}</Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="mb-3">
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={3}><Form.Control placeholder="Filter by name or employee no." value={employeeFilters.name} onChange={(e) => { setEmployeeFilters({ ...employeeFilters, name: e.target.value }); setPage("employees", 1); }} /></Col>
                            <Col md={3}>{renderNativeSelect(employeeFilters.store, (value) => { setEmployeeFilters({ ...employeeFilters, store: value }); setPage("employees", 1); }, stores, "Filter by store")}</Col>
                            <Col md={3}>{renderNativeSelect(employeeFilters.department, (value) => { setEmployeeFilters({ ...employeeFilters, department: value }); setPage("employees", 1); }, departments, "Filter by department")}</Col>
                            <Col md={3}>{renderNativeSelect(employeeFilters.salary_structure, (value) => { setEmployeeFilters({ ...employeeFilters, salary_structure: value }); setPage("employees", 1); }, salaryStructures, "Filter by salary structure")}</Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Table responsive bordered hover>
                    <thead>
                        <tr>
                            <th>Employee</th>
                            <th>Employment</th>
                            <th>Store</th>
                            <th>Department / Role</th>
                            <th>Salary Structure</th>
                            <th>Supervisor</th>
                            <th>Docs</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((employee) => (
                            <tr key={employee.id}>
                                <td>
                                    <strong>{employee.full_name}</strong>
                                    <div className="text-muted small">{employee.employee_number} / ID {employee.id_number || "Missing"}</div>
                                </td>
                                <td>{employee.employment_type}</td>
                                <td>{employee.store_name}</td>
                                <td>{employee.department_name} / {employee.role_title}</td>
                                <td>{employee.salary_structure_name}</td>
                                <td>{employee.supervisor_name || "-"}</td>
                                <td>
                                    <Badge bg={employee.kra_pin ? "success" : "danger"} className="me-1">KRA</Badge>
                                    <Badge bg={employee.sha_number ? "success" : "danger"} className="me-1">SHA</Badge>
                                    <Badge bg={employee.nssf_number ? "success" : "danger"}>NSSF</Badge>
                                </td>
                                <td><Badge bg={employee.status === "ACTIVE" ? "success" : "warning"}>{employee.status}</Badge></td>
                                <td><Button size="sm" variant="outline-primary" onClick={() => startEmployeeEdit(employee)}>Edit</Button></td>
                            </tr>
                        ))}
                        {!filteredEmployees.length && <tr><td colSpan={9} className="text-center text-muted">No employees found.</td></tr>}
                    </tbody>
                </Table>
                {renderPagination("employees", totalPages, currentPage)}
            </>
        );
    };

    const buildAttendanceBatch = () => {
        const targetEmployees = employees.filter((employee) => {
            const active = employee.status === "ACTIVE" || employee.status === "ON_LEAVE";
            const storeMatch = !attendanceSetup.store || String(employee.store) === String(attendanceSetup.store);
            return active && storeMatch;
        });
        setAttendanceBatch(targetEmployees.map((employee) => ({
            employee: employee.id,
            employee_name: employee.full_name,
            store: attendanceSetup.store || employee.store,
            store_name: employee.store_name,
            attendance_date: attendanceSetup.attendance_date,
            shift_name: attendanceSetup.shift_name,
            late_minutes: "0",
            overtime_hours: "0",
            is_absent: false,
            absence_reason: "",
        })));
    };

    const saveAttendanceBatch = () => submit(async () => {
        await Promise.all(attendanceBatch.map(async (row) => {
            const existing = attendance.find((record) =>
                String(record.employee) === String(row.employee) &&
                record.attendance_date === row.attendance_date &&
                (record.shift_name || "") === (row.shift_name || "")
            );
            const payload = {
                ...row,
                late_minutes: row.is_absent ? "0" : row.late_minutes,
                overtime_hours: row.is_absent ? "0" : row.overtime_hours,
            };
            if (existing?.id) {
                await updateHRAttendance(existing.id, payload);
            } else {
                await createHRAttendance(payload);
            }
        }));
        setAttendanceBatch([]);
    }, "Attendance saved.");

    const startAttendanceEdit = (record) => {
        setAttendanceEditId(record.id);
        setAttendanceEditForm({
            employee: record.employee,
            employee_name: record.employee_name,
            store: record.store,
            store_name: record.store_name,
            attendance_date: record.attendance_date,
            shift_name: record.shift_name || "",
            late_minutes: record.late_minutes || "0",
            overtime_hours: record.overtime_hours || "0",
            is_absent: Boolean(record.is_absent),
            absence_reason: record.absence_reason || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const cancelAttendanceEdit = () => {
        setAttendanceEditId(null);
        setAttendanceEditForm(null);
    };

    const saveAttendanceEdit = () => submit(async () => {
        await updateHRAttendance(attendanceEditId, {
            ...attendanceEditForm,
            late_minutes: attendanceEditForm.is_absent ? "0" : attendanceEditForm.late_minutes,
            overtime_hours: attendanceEditForm.is_absent ? "0" : attendanceEditForm.overtime_hours,
        });
        cancelAttendanceEdit();
    }, "Attendance updated.");

    const renderAttendance = () => {
        const { currentPage, totalPages, pageRows } = getPageData("attendance", attendance);
        return (
            <>
                <Card className="mb-3">
                    <Card.Body>
                        <Card.Title>Auto Fill Attendance</Card.Title>
                        <Row className="g-3 align-items-end">
                            <Col md={3}>
                                <Form.Label>Attendance date</Form.Label>
                                <Form.Control type="date" value={attendanceSetup.attendance_date} onChange={(e) => setAttendanceSetup({ ...attendanceSetup, attendance_date: e.target.value })} />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Store</Form.Label>
                                {renderNativeSelect(attendanceSetup.store, (value) => setAttendanceSetup({ ...attendanceSetup, store: value }), stores, "All active staff or select store")}
                            </Col>
                            <Col md={3}>
                                <Form.Label>Work time / shift</Form.Label>
                                <Form.Select value={attendanceSetup.shift_name} onChange={(e) => setAttendanceSetup({ ...attendanceSetup, shift_name: e.target.value })}>
                                    <option value="">Default work time - no shift</option>
                                    <option value="A">Shift A - 8 hours</option>
                                    <option value="B">Shift B - 8 hours</option>
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Button disabled={!attendanceSetup.attendance_date || saving} onClick={buildAttendanceBatch}>Auto Fill Present</Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {isAdmin && attendanceEditId && attendanceEditForm && (
                    <Card className="mb-3">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Card.Title className="mb-0">Edit Attendance</Card.Title>
                                <Button variant="outline-secondary" size="sm" onClick={cancelAttendanceEdit}>Cancel Edit</Button>
                            </div>
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Label>Employee</Form.Label>
                                    <Form.Control value={attendanceEditForm.employee_name || ""} disabled />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Date</Form.Label>
                                    <Form.Control type="date" value={attendanceEditForm.attendance_date} onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, attendance_date: e.target.value })} />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Work time / shift</Form.Label>
                                    <Form.Select value={attendanceEditForm.shift_name} onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, shift_name: e.target.value })}>
                                        <option value="">Default work time - no shift</option>
                                        <option value="A">Shift A - 8 hours</option>
                                        <option value="B">Shift B - 8 hours</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Late minutes</Form.Label>
                                    <Form.Control type="number" min="0" disabled={attendanceEditForm.is_absent} value={attendanceEditForm.late_minutes} onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, late_minutes: e.target.value })} />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Overtime hours</Form.Label>
                                    <Form.Control type="number" min="0" step="0.01" disabled={attendanceEditForm.is_absent} value={attendanceEditForm.overtime_hours} onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, overtime_hours: e.target.value })} />
                                </Col>
                                <Col md={1}>
                                    <Form.Check label="Absent" checked={attendanceEditForm.is_absent} onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, is_absent: e.target.checked })} />
                                </Col>
                                <Col md={10}>
                                    <Form.Label>Absence reason</Form.Label>
                                    <Form.Control placeholder="Optional absence reason" value={attendanceEditForm.absence_reason} onChange={(e) => setAttendanceEditForm({ ...attendanceEditForm, absence_reason: e.target.value })} />
                                </Col>
                                <Col md={2}>
                                    <Button disabled={saving} onClick={saveAttendanceEdit}>Update Attendance</Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                )}

                {!!attendanceBatch.length && (
                    <Card className="mb-3">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Card.Title className="mb-0">Mark Exceptions</Card.Title>
                                <Button disabled={saving} onClick={saveAttendanceBatch}>Save Attendance Batch</Button>
                            </div>
                            <Table responsive bordered hover>
                                <thead><tr><th>Employee</th><th>Store</th><th>Absent</th><th>Late minutes</th><th>Overtime hours</th><th>Absence reason</th></tr></thead>
                                <tbody>
                                    {attendanceBatch.map((row, index) => (
                                        <tr key={`${row.employee}-${index}`}>
                                            <td>{row.employee_name}</td>
                                            <td>{row.store_name}</td>
                                            <td>
                                                <Form.Check
                                                    label="Absent"
                                                    checked={row.is_absent}
                                                    onChange={(e) => setAttendanceBatch((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, is_absent: e.target.checked } : item))}
                                                />
                                            </td>
                                            <td><Form.Control type="number" min="0" placeholder="Late minutes" disabled={row.is_absent} value={row.late_minutes} onChange={(e) => setAttendanceBatch((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, late_minutes: e.target.value } : item))} /></td>
                                            <td><Form.Control type="number" min="0" step="0.01" placeholder="Overtime hours" disabled={row.is_absent} value={row.overtime_hours} onChange={(e) => setAttendanceBatch((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, overtime_hours: e.target.value } : item))} /></td>
                                            <td><Button size="sm" variant={row.absence_reason ? "outline-success" : "outline-secondary"} onClick={() => setAbsenceModal({ index, reason: row.absence_reason })}>Reason</Button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                )}

                <Table responsive bordered hover>
                    <thead><tr><th>Employee</th><th>Store</th><th>Date</th><th>Work Time</th><th>Late</th><th>Overtime</th><th>Absent</th><th>Reason</th><th>Payroll</th>{isAdmin && <th>Action</th>}</tr></thead>
                    <tbody>
                        {pageRows.map((record) => (
                            <tr key={record.id}>
                                <td>{record.employee_name}</td>
                                <td>{record.store_name}</td>
                                <td>{record.attendance_date}</td>
                                <td>{record.shift_name || "-"}</td>
                                <td><Badge bg={Number(record.late_minutes) > 10 ? "danger" : "success"}>{record.late_minutes} min</Badge></td>
                                <td>{record.overtime_hours} hrs</td>
                                <td>{record.is_absent ? "Yes" : "No"}</td>
                                <td>{record.absence_reason || "-"}</td>
                                <td>{record.payroll_processed ? "Processed" : "Open"}</td>
                                {isAdmin && <td><Button size="sm" variant="outline-primary" onClick={() => startAttendanceEdit(record)}>Edit</Button></td>}
                            </tr>
                        ))}
                        {!attendance.length && <tr><td colSpan={isAdmin ? 10 : 9} className="text-center text-muted">No attendance records yet.</td></tr>}
                    </tbody>
                </Table>
                {renderPagination("attendance", totalPages, currentPage)}

                <Modal show={!!absenceModal} onHide={() => setAbsenceModal(null)}>
                    <Modal.Header closeButton><Modal.Title>Absence Reason</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <Form.Control
                            as="textarea"
                            rows={5}
                            placeholder="Optional reason for absence"
                            value={absenceModal?.reason || ""}
                            onChange={(e) => setAbsenceModal((current) => ({ ...current, reason: e.target.value }))}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => setAbsenceModal(null)}>Cancel</Button>
                        <Button onClick={() => {
                            setAttendanceBatch((current) => current.map((row, index) => index === absenceModal.index ? { ...row, absence_reason: absenceModal.reason } : row));
                            setAbsenceModal(null);
                        }}>Save Reason</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    };

    const saveLeaveType = () => submit(async () => {
        if (leaveTypeEditId) {
            await updateHRLeaveType(leaveTypeEditId, leaveTypeForm);
        } else {
            await createHRLeaveType(leaveTypeForm);
        }
        setLeaveTypeEditId(null);
        setLeaveTypeForm({ name: "", annual_entitlement_days: "21", statutory_minimum_days: "21", is_paid: true });
    }, leaveTypeEditId ? "Leave type updated." : "Leave type saved.");

    const renderLeave = () => {
        const { currentPage, totalPages, pageRows } = getPageData("leaveRequests", leaveRequests);
        return (
            <>
                <Card className="mb-3">
                    <Card.Body>
                        <Card.Title>Leave Type</Card.Title>
                        <Row className="g-3 align-items-end">
                            <Col md={4}><Form.Label>Leave type name</Form.Label><Form.Control placeholder="Annual leave, sick leave, maternity leave..." value={leaveTypeForm.name} onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, name: e.target.value })} /></Col>
                            <Col md={2}><Form.Label>Annual days</Form.Label><Form.Control type="number" placeholder="Entitlement days" value={leaveTypeForm.annual_entitlement_days} onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, annual_entitlement_days: e.target.value })} /></Col>
                            <Col md={2}><Form.Label>Statutory days</Form.Label><Form.Control type="number" placeholder="Legal minimum" value={leaveTypeForm.statutory_minimum_days} onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, statutory_minimum_days: e.target.value })} /></Col>
                            <Col md={2}><Form.Check label="Paid leave" checked={leaveTypeForm.is_paid} onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, is_paid: e.target.checked })} /></Col>
                            <Col md={2}><Button disabled={saving} onClick={saveLeaveType}>{leaveTypeEditId ? "Update Type" : "Save Type"}</Button></Col>
                        </Row>
                        <Table responsive bordered hover className="mt-3 mb-0">
                            <thead><tr><th>Kenya leave setup</th><th>Days</th><th>Statutory</th><th>Use</th><th>Action</th></tr></thead>
                            <tbody>
                                {kenyaLeaveTypes.map((leave) => (
                                    <tr key={leave.name}>
                                        <td>{leave.name}</td>
                                        <td>{leave.annual}</td>
                                        <td>{leave.statutory}</td>
                                        <td>{leave.note}</td>
                                        <td><Button size="sm" variant="outline-primary" onClick={() => setLeaveTypeForm({ name: leave.name, annual_entitlement_days: leave.annual, statutory_minimum_days: leave.statutory, is_paid: leave.name !== "Unpaid Leave" })}>Use</Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <div className="mt-3">
                            {leaveTypes.map((leaveType) => (
                                <Badge key={leaveType.id} bg="secondary" className="me-2 mb-2" role="button" onClick={() => {
                                    setLeaveTypeEditId(leaveType.id);
                                    setLeaveTypeForm({
                                        name: leaveType.name,
                                        annual_entitlement_days: leaveType.annual_entitlement_days,
                                        statutory_minimum_days: leaveType.statutory_minimum_days,
                                        is_paid: leaveType.is_paid,
                                    });
                                }}>{leaveType.name}</Badge>
                            ))}
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mb-3">
                    <Card.Body>
                        <Card.Title>Leave Request</Card.Title>
                        <Row className="g-3">
                            <Col md={5}>
                                <Form.Label>Employee</Form.Label>
                                {renderSearchableSelect({ value: leaveForm.employee, onChange: (value) => setLeaveForm({ ...leaveForm, employee: value }), options: employees, placeholder: "Search employee name", labelKey: "full_name" })}
                            </Col>
                            <Col md={4}>
                                <Form.Label>Leave type</Form.Label>
                                {renderNativeSelect(leaveForm.leave_type, (value) => setLeaveForm({ ...leaveForm, leave_type: value }), leaveTypes, "Select leave type")}
                            </Col>
                            <Col md={3}>
                                <Form.Label>Days requested</Form.Label>
                                <Form.Control type="number" step="0.5" placeholder="Requested days" value={leaveForm.days_requested} onChange={(e) => setLeaveForm({ ...leaveForm, days_requested: e.target.value })} />
                            </Col>
                            <Col md={3}><Form.Label>Start date</Form.Label><Form.Control type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} /></Col>
                            <Col md={3}><Form.Label>End date</Form.Label><Form.Control type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} /></Col>
                            <Col md={3}><Form.Label>Balance before</Form.Label><Form.Control type="number" step="0.5" placeholder="Current balance" value={leaveForm.balance_before} onChange={(e) => setLeaveForm({ ...leaveForm, balance_before: e.target.value })} /></Col>
                            <Col md={3}><Form.Label>Balance after</Form.Label><Form.Control type="number" step="0.5" placeholder="Balance after request" value={leaveForm.balance_after} onChange={(e) => setLeaveForm({ ...leaveForm, balance_after: e.target.value })} /></Col>
                            <Col md={12}><Form.Label>Reason</Form.Label><Form.Control as="textarea" rows={3} placeholder="Leave request reason" value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} /></Col>
                            <Col md={12}><Button disabled={saving} onClick={() => submit(async () => {
                                await createHRLeaveRequest(leaveForm);
                                setLeaveForm({ employee: "", leave_type: "", start_date: "", end_date: "", days_requested: "", balance_before: "0", balance_after: "0", reason: "" });
                            }, "Leave request saved.")}>Save Leave Request</Button></Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Table responsive bordered hover>
                    <thead><tr><th>Employee</th><th>Store</th><th>Type</th><th>Dates</th><th>Days</th><th>Balance</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                        {pageRows.map((request) => (
                            <tr key={request.id}>
                                <td>{request.employee_name}</td>
                                <td>{request.store_name}</td>
                                <td>{request.leave_type_name}</td>
                                <td>{request.start_date} to {request.end_date}</td>
                                <td>{request.days_requested}</td>
                                <td>{request.balance_before} to {request.balance_after}</td>
                                <td><Badge bg={request.status === "APPROVED" ? "success" : request.status === "REJECTED" ? "danger" : "warning"}>{request.status}</Badge></td>
                                <td>
                                    {request.status === "PENDING" ? (
                                        <>
                                            <Button size="sm" variant="outline-success" className="me-1" disabled={saving} onClick={() => submit(async () => approveHRLeaveRequest(request.id), "Leave approved.")}>Approve</Button>
                                            <Button size="sm" variant="outline-danger" disabled={saving} onClick={() => submit(async () => rejectHRLeaveRequest(request.id), "Leave rejected.")}>Reject</Button>
                                        </>
                                    ) : request.status === "REJECTED" ? (
                                        <span className="text-muted small">New request required</span>
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!leaveRequests.length && <tr><td colSpan={8} className="text-center text-muted">No leave requests yet.</td></tr>}
                    </tbody>
                </Table>
                {renderPagination("leaveRequests", totalPages, currentPage)}
            </>
        );
    };

    const savePayrollPeriod = () => submit(async () => {
        if (payrollPeriodEditId) {
            await updateHRPayrollPeriod(payrollPeriodEditId, payrollPeriodForm);
        } else {
            await createHRPayrollPeriod(payrollPeriodForm);
        }
        setPayrollPeriodEditId(null);
        setPayrollPeriodForm(blankPayrollPeriod);
    }, payrollPeriodEditId ? "Payroll period updated." : "Payroll period saved.");

    const savePayrollItem = () => submit(async () => {
        if (payrollItemEditId) {
            await updateHRPayrollItem(payrollItemEditId, payrollItemForm);
        } else {
            await createHRPayrollItem(payrollItemForm);
        }
        setPayrollItemEditId(null);
        setPayrollItemForm(blankPayrollItem);
    }, payrollItemEditId ? "Payroll line updated." : "Payroll line saved.");

    const cancelPayrollItemEdit = () => {
        setPayrollItemEditId(null);
        setPayrollItemForm(blankPayrollItem);
    };

    const selectedPayrollPeriod = useMemo(
        () => payrollPeriods.find((period) => String(period.id) === String(payrollItemForm.period)),
        [payrollItemForm.period, payrollPeriods]
    );
    const payrollLineLocked = selectedPayrollPeriod && !["DRAFT", "APPROVED"].includes(selectedPayrollPeriod.status);

    const payrollStatusVariant = (statusValue) => {
        if (statusValue === "DRAFT") return "secondary";
        if (statusValue === "APPROVED") return "primary";
        if (statusValue === "POSTED") return "warning";
        if (statusValue === "PAID") return "success";
        if (statusValue === "REVERSED") return "danger";
        return "secondary";
    };

    const runPayrollPeriodAction = (period, actionName) => {
        const actions = {
            approve: () => approveHRPayrollPeriod(period.id),
            post: () => postHRPayrollPeriodToAccounting(period.id),
            pay: () => markHRPayrollPeriodPaid(period.id, payrollPaymentAccount),
            reverse: () => reverseHRPayrollPeriodPosting(period.id),
            reopen: () => reopenHRPayrollPeriod(period.id),
        };
        if (actionName === "pay" && !payrollPaymentAccount) {
            setMessage({ type: "warning", text: "Select a payment account before marking payroll as paid." });
            return;
        }
        const messages = {
            approve: "Payroll approved.",
            post: "Payroll posted to accounting.",
            pay: "Payroll marked as paid.",
            reverse: "Payroll posting reversed.",
            reopen: "Payroll reopened.",
        };
        submit(actions[actionName], messages[actionName]);
    };

    const savePayrollMapping = () => submit(async () => {
        const payload = { ...payrollMappingForm };
        delete payload.id;
        if (payrollMappingForm.id) {
            await updateHRPayrollAccountMapping(payrollMappingForm.id, payload);
        } else {
            await createHRPayrollAccountMapping(payload);
        }
        setPayrollMappingForm(blankPayrollMapping);
    }, payrollMappingForm.id ? "Payroll account mapping updated." : "Payroll account mapping saved.");

    const renderPayroll = () => {
        const { currentPage, totalPages, pageRows } = getPageData("payrollItems", payrollItems);
        return (
            <>
                <Row className="g-3 mb-3">
                    <Col lg={5}>
                        <Card>
                            <Card.Body>
                                <Card.Title>Payroll Period</Card.Title>
                                <Row className="g-3">
                                    <Col md={12}><Form.Label>Period name</Form.Label><Form.Control placeholder="May 2026 Payroll" value={payrollPeriodForm.name} onChange={(e) => setPayrollPeriodForm({ ...payrollPeriodForm, name: e.target.value })} /></Col>
                                    <Col md={6}><Form.Label>Start date</Form.Label><Form.Control type="date" value={payrollPeriodForm.start_date} onChange={(e) => setPayrollPeriodForm({ ...payrollPeriodForm, start_date: e.target.value })} /></Col>
                                    <Col md={6}><Form.Label>End date</Form.Label><Form.Control type="date" value={payrollPeriodForm.end_date} onChange={(e) => setPayrollPeriodForm({ ...payrollPeriodForm, end_date: e.target.value })} /></Col>
                                    <Col md={6}>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select value={payrollPeriodForm.status} onChange={(e) => setPayrollPeriodForm({ ...payrollPeriodForm, status: e.target.value })}>
                                            <option value="DRAFT">Draft</option>
                                            <option value="APPROVED">Approved</option>
                                            <option value="POSTED" disabled>Posted</option>
                                            <option value="PAID" disabled>Paid</option>
                                            <option value="REVERSED" disabled>Reversed</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label>Posting mode</Form.Label>
                                        <Form.Select value={payrollPeriodForm.payroll_posting_mode || "STANDARD"}
                                                     onChange={(e) => setPayrollPeriodForm({ ...payrollPeriodForm, payroll_posting_mode: e.target.value })}>
                                            <option value="STANDARD">Standard accounts</option>
                                            <option value="LEGACY">Legacy mapped accounts</option>
                                            <option value="MIXED">Mapped where available</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={6} className="d-flex align-items-end"><Button disabled={saving} onClick={savePayrollPeriod}>{payrollPeriodEditId ? "Update Period" : "Save Period"}</Button></Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={7}>
                        <Card>
                            <Card.Body>
                                <Card.Title>Payroll Setup Examples</Card.Title>
                                <Row>
                                    <Col md={6}>
                                        <strong>Periods</strong>
                                        <ul className="mb-0">{payrollPeriodExamples.map((item) => <li key={item}>{item}</li>)}</ul>
                                    </Col>
                                    <Col md={6}>
                                        <strong>Lines</strong>
                                        <ul className="mb-0">{payrollLineExamples.map((item) => <li key={item}>{item}</li>)}</ul>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card className="mb-3">
                    <Card.Body>
                        <Card.Title>Payroll Period Actions</Card.Title>
                        <Table responsive bordered hover className="mb-0">
                            <thead><tr><th>Period</th><th>Dates</th><th>Mode</th><th>Status</th><th>Posted</th><th>Paid</th><th>Action</th></tr></thead>
                            <tbody>
                                {payrollPeriods.map((period) => (
                                    <tr key={period.id}>
                                        <td>{period.name}</td>
                                        <td>{period.start_date} to {period.end_date}</td>
                                        <td>{period.payroll_posting_mode || "STANDARD"}</td>
                                        <td><Badge bg={payrollStatusVariant(period.status)}>{period.status}</Badge></td>
                                        <td>{period.accounting_transaction_number || "-"}</td>
                                        <td>{period.payment_transaction_number || "-"}</td>
                                        <td>
                                            <Button size="sm" variant="outline-primary" className="me-1" disabled={saving || period.status !== "DRAFT"} onClick={() => runPayrollPeriodAction(period, "approve")}>Approve Payroll</Button>
                                            <Button size="sm" variant="outline-warning" className="me-1" disabled={saving || period.status !== "APPROVED"} onClick={() => runPayrollPeriodAction(period, "post")}>Post to Accounting</Button>
                                            <Button size="sm" variant="outline-success" className="me-1" disabled={saving || period.status !== "POSTED" || !payrollPaymentAccount} onClick={() => runPayrollPeriodAction(period, "pay")}>Mark as Paid</Button>
                                            <Button size="sm" variant="outline-danger" className="me-1" disabled={saving || period.status !== "POSTED"} onClick={() => runPayrollPeriodAction(period, "reverse")}>Reverse Posting</Button>
                                            <Button size="sm" variant="outline-secondary" disabled={saving || period.status !== "REVERSED"} onClick={() => runPayrollPeriodAction(period, "reopen")}>Reopen</Button>
                                        </td>
                                    </tr>
                                ))}
                                {!payrollPeriods.length && <tr><td colSpan={7} className="text-center text-muted">No payroll periods yet.</td></tr>}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                <Card className="mb-3">
                    <Card.Body>
                        <Row className="g-3 align-items-end">
                            <Col md={6}>
                                <Form.Label>Payroll payment account</Form.Label>
                                {renderNativeSelect(payrollPaymentAccount, setPayrollPaymentAccount, accounts, "Select cash, bank, or Mpesa account", "account_name")}
                            </Col>
                            <Col md={6}>
                                <div className="text-muted small">Payment creates the salary payment journal after payroll has been posted.</div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="mb-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Card.Title className="mb-0">Payroll Accounting Settings</Card.Title>
                            {payrollMappingForm.id && <Button size="sm" variant="outline-secondary" onClick={() => setPayrollMappingForm(blankPayrollMapping)}>Cancel Edit</Button>}
                        </div>
                        <Row className="g-3">
                            <Col md={4}><Form.Label>Employee</Form.Label>{renderSearchableSelect({ value: payrollMappingForm.employee, onChange: (value) => setPayrollMappingForm({ ...payrollMappingForm, employee: value }), options: employees, placeholder: "Search employee", labelKey: "full_name" })}</Col>
                            <Col md={4}><Form.Label>Legacy salary expense</Form.Label>{renderNativeSelect(payrollMappingForm.legacy_salary_expense_account, (value) => setPayrollMappingForm({ ...payrollMappingForm, legacy_salary_expense_account: value }), accounts, "Optional legacy salary account", "account_name")}</Col>
                            <Col md={4}><Form.Label>Staff advance account</Form.Label>{renderNativeSelect(payrollMappingForm.staff_advance_account, (value) => setPayrollMappingForm({ ...payrollMappingForm, staff_advance_account: value }), accounts, "Optional staff advance account", "account_name")}</Col>
                            <Col md={4}><Form.Label>Default expense account</Form.Label>{renderNativeSelect(payrollMappingForm.default_payroll_expense_account, (value) => setPayrollMappingForm({ ...payrollMappingForm, default_payroll_expense_account: value }), accounts, "Optional default expense account", "account_name")}</Col>
                            <Col md={4}><Form.Label>Payroll payable</Form.Label>{renderNativeSelect(payrollMappingForm.payroll_payable_account, (value) => setPayrollMappingForm({ ...payrollMappingForm, payroll_payable_account: value }), accounts, "Optional payroll payable override", "account_name")}</Col>
                            <Col md={4}><Form.Label>PAYE payable</Form.Label>{renderNativeSelect(payrollMappingForm.paye_payable_account, (value) => setPayrollMappingForm({ ...payrollMappingForm, paye_payable_account: value }), accounts, "Optional PAYE payable override", "account_name")}</Col>
                            <Col md={4}><Form.Label>SHA payable</Form.Label>{renderNativeSelect(payrollMappingForm.sha_payable_account, (value) => setPayrollMappingForm({ ...payrollMappingForm, sha_payable_account: value }), accounts, "Optional SHA payable override", "account_name")}</Col>
                            <Col md={4}><Form.Label>NSSF payable</Form.Label>{renderNativeSelect(payrollMappingForm.nssf_payable_account, (value) => setPayrollMappingForm({ ...payrollMappingForm, nssf_payable_account: value }), accounts, "Optional NSSF payable override", "account_name")}</Col>
                            <Col md={4} className="d-flex align-items-end"><Button disabled={saving || !payrollMappingForm.employee} onClick={savePayrollMapping}>{payrollMappingForm.id ? "Update Mapping" : "Save Mapping"}</Button></Col>
                        </Row>
                        <Table responsive bordered hover size="sm" className="mt-3 mb-0">
                            <thead><tr><th>Employee</th><th>Legacy Expense</th><th>Advance</th><th>Default Expense</th><th>Action</th></tr></thead>
                            <tbody>
                                {payrollMappings.map((mapping) => (
                                    <tr key={mapping.id}>
                                        <td>{mapping.employee_name}</td>
                                        <td>{mapping.legacy_salary_expense_account_name || "-"}</td>
                                        <td>{mapping.staff_advance_account_name || "-"}</td>
                                        <td>{mapping.default_payroll_expense_account_name || "-"}</td>
                                        <td><Button size="sm" variant="outline-primary" onClick={() => setPayrollMappingForm({ ...blankPayrollMapping, ...mapping })}>Edit</Button></td>
                                    </tr>
                                ))}
                                {!payrollMappings.length && <tr><td colSpan={5} className="text-center text-muted">No payroll account mappings yet.</td></tr>}
                            </tbody>
                        </Table>
                        <Row className="g-3 mt-2">
                            <Col md={4}><Alert variant="info" className="mb-0">Employees without mappings: {(payrollTransitionReport.employees_without_mappings || []).length}</Alert></Col>
                            <Col md={4}><Alert variant="secondary" className="mb-0">Legacy payroll accounts: {(payrollTransitionReport.legacy_payroll_accounts || []).length}</Alert></Col>
                            <Col md={4}><Alert variant="secondary" className="mb-0">Advance mappings: {(payrollTransitionReport.advance_account_mappings || []).length}</Alert></Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="mb-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Card.Title className="mb-0">{payrollItemEditId ? "Edit Payroll Line" : "Payroll Line"}</Card.Title>
                            {payrollItemEditId && <Button variant="outline-secondary" size="sm" onClick={cancelPayrollItemEdit}>Cancel Edit</Button>}
                        </div>
                        <Row className="g-3">
                            <Col md={4}><Form.Label>Period</Form.Label>{renderNativeSelect(payrollItemForm.period, (value) => setPayrollItemForm({ ...payrollItemForm, period: value }), payrollPeriods, "Select payroll period")}</Col>
                            <Col md={4}><Form.Label>Employee</Form.Label>{renderSearchableSelect({ value: payrollItemForm.employee, onChange: (value) => setPayrollItemForm({ ...payrollItemForm, employee: value }), options: employees, placeholder: "Search employee name", labelKey: "full_name" })}</Col>
                            <Col md={4}><Form.Label>Store</Form.Label>{renderNativeSelect(payrollItemForm.store, (value) => setPayrollItemForm({ ...payrollItemForm, store: value }), stores, "Select payroll store")}</Col>
                            {["basic_pay", "allowances", "overtime_pay", "deductions", "paye", "sha", "nssf", "net_pay"].map((field) => (
                                <Col md={3} key={field}>
                                    <Form.Label>{field.replaceAll("_", " ").toUpperCase()}</Form.Label>
                                    <Form.Control type="number" step="0.01" placeholder={field.replaceAll("_", " ")} value={payrollItemForm[field]} onChange={(e) => setPayrollItemForm({ ...payrollItemForm, [field]: e.target.value })} />
                                </Col>
                            ))}
                            <Col md={12}><Button disabled={saving || payrollLineLocked} onClick={savePayrollItem}>{payrollItemEditId ? "Update Payroll Line" : "Save Payroll Line"}</Button></Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Table responsive bordered hover>
                    <thead><tr><th>Period</th><th>Employee</th><th>Store</th><th>Basic</th><th>Allowances</th><th>Deductions</th><th>PAYE/SHA/NSSF</th><th>Net</th><th>Action</th></tr></thead>
                    <tbody>
                        {pageRows.map((item) => (
                            <tr key={item.id}>
                                <td>{item.period_name}</td>
                                <td>{item.employee_name}</td>
                                <td>{item.store_name}</td>
                                <td>{currency(item.basic_pay)}</td>
                                <td>{currency(item.allowances)}</td>
                                <td>{currency(item.deductions)}</td>
                                <td>{currency(Number(item.paye || 0) + Number(item.sha || 0) + Number(item.nssf || 0))}</td>
                                <td><strong>{currency(item.net_pay)}</strong></td>
                                <td>
                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => {
                                        setPayrollItemEditId(item.id);
                                        setPayrollItemForm({ ...payrollItemForm, ...item });
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}>Edit</Button>
                                    {item.is_locked || !["DRAFT", "APPROVED"].includes(item.period_status) ? (
                                        <Badge bg="secondary">Locked</Badge>
                                    ) : (
                                        <Badge bg="light" text="dark">Open</Badge>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!payrollItems.length && <tr><td colSpan={9} className="text-center text-muted">No payroll lines yet.</td></tr>}
                    </tbody>
                </Table>
                {renderPagination("payrollItems", totalPages, currentPage)}
            </>
        );
    };

    const filteredPerformanceReviews = useMemo(() => performanceReviews.filter((review) => {
        const employeeMatch = !performanceFilters.employee || String(review.employee) === String(performanceFilters.employee);
        const storeMatch = !performanceFilters.store || employees.some((employee) => String(employee.id) === String(review.employee) && String(employee.store) === String(performanceFilters.store));
        return employeeMatch && storeMatch;
    }), [employees, performanceFilters, performanceReviews]);

    const performanceAggregates = useMemo(() => {
        return employees.map((employee) => {
            const reviews = performanceReviews.filter((review) => String(review.employee) === String(employee.id));
            if (!reviews.length) return null;
            const average = reviews.reduce((total, review) => total + reviewScore(review), 0) / reviews.length;
            return { employee, average, count: reviews.length, flag: performanceFlag(average) };
        }).filter(Boolean).sort((a, b) => b.average - a.average);
    }, [employees, performanceReviews]);

    const renderPerformance = () => {
        const { currentPage, totalPages, pageRows } = getPageData("performanceReviews", filteredPerformanceReviews);
        return (
            <>
                <Card className="mb-3">
                    <Card.Body>
                        <Card.Title>Performance Review</Card.Title>
                        <Row className="g-3">
                            <Col md={4}><Form.Label>Employee</Form.Label>{renderSearchableSelect({ value: performanceForm.employee, onChange: (value) => setPerformanceForm({ ...performanceForm, employee: value }), options: employees, placeholder: "Search employee name", labelKey: "full_name" })}</Col>
                            <Col md={2}><Form.Label>Period start</Form.Label><Form.Control type="date" value={performanceForm.period_start} onChange={(e) => setPerformanceForm({ ...performanceForm, period_start: e.target.value })} /></Col>
                            <Col md={2}><Form.Label>Period end</Form.Label><Form.Control type="date" value={performanceForm.period_end} onChange={(e) => setPerformanceForm({ ...performanceForm, period_end: e.target.value })} /></Col>
                            <Col md={2}><Form.Label>Attendance score</Form.Label><Form.Control type="number" min="0" max="100" placeholder="0 to 100" value={performanceForm.attendance_score} onChange={(e) => setPerformanceForm({ ...performanceForm, attendance_score: e.target.value })} /></Col>
                            <Col md={2}><Form.Label>Late days</Form.Label><Form.Control type="number" min="0" placeholder="Number of late days" value={performanceForm.late_days} onChange={(e) => setPerformanceForm({ ...performanceForm, late_days: e.target.value })} /></Col>
                            <Col md={3}><Form.Label>Productivity score</Form.Label><Form.Control type="number" min="0" max="100" placeholder="0 to 100" value={performanceForm.productivity_score} onChange={(e) => setPerformanceForm({ ...performanceForm, productivity_score: e.target.value })} /></Col>
                            <Col md={3}><Form.Label>Quality of work score</Form.Label><Form.Control type="number" min="0" max="100" placeholder="0 to 100" value={performanceForm.quality_score} onChange={(e) => setPerformanceForm({ ...performanceForm, quality_score: e.target.value })} /></Col>
                            <Col md={3}><Form.Label>Initiative / innovation score</Form.Label><Form.Control type="number" min="0" max="100" placeholder="0 to 100" value={performanceForm.initiative_score} onChange={(e) => setPerformanceForm({ ...performanceForm, initiative_score: e.target.value })} /></Col>
                            <Col md={6}><Form.Label>Review notes</Form.Label><Form.Control as="textarea" rows={3} placeholder="Specific observation or review note" value={performanceForm.notes} onChange={(e) => setPerformanceForm({ ...performanceForm, notes: e.target.value })} /></Col>
                            <Col md={12}><Form.Label>Additional information</Form.Label><Form.Control as="textarea" rows={5} placeholder="Warning, coaching, promotion note, salary increment note, or other HR follow-up" value={performanceForm.action} onChange={(e) => setPerformanceForm({ ...performanceForm, action: e.target.value })} /></Col>
                            <Col md={12}><Button disabled={saving} onClick={() => submit(async () => {
                                await createHRPerformanceReview(performanceForm);
                                setPerformanceForm({ employee: "", period_start: "", period_end: "", attendance_score: "80", late_days: "0", productivity_score: "80", quality_score: "80", initiative_score: "80", action: "", notes: "" });
                            }, "Performance review saved.")}>Save Review</Button></Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Row className="g-3 mb-3">
                    <Col lg={6}>
                        <Card>
                            <Card.Body>
                                <Card.Title>Scoring Guide</Card.Title>
                                <p className="mb-2">The overall score is the average of attendance, productivity, quality of work,
                                    and initiative / innovation.</p>
                                <p className="mb-0">Late days remain visible as context only.</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={6}>
                        <Card>
                            <Card.Body>
                                <Card.Title>Aggregate Flags</Card.Title>
                                <Table responsive size="sm" className="mb-0">
                                    <tbody>
                                        <tr><td>90 and above</td><td><Badge bg="success">Promotion eligible</Badge></td></tr>
                                        <tr><td>85 to 89</td><td><Badge bg="primary">Salary increment eligible</Badge></td></tr>
                                        <tr><td>50 to 64</td><td><Badge bg="warning">Warning review</Badge></td></tr>
                                        <tr><td>Below 50</td><td><Badge bg="danger">Dismissal review</Badge></td></tr>
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card className="mb-3">
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={6}>{renderSearchableSelect({ value: performanceFilters.employee, onChange: (value) => { setPerformanceFilters({ ...performanceFilters, employee: value }); setPage("performanceReviews", 1); }, options: employees, placeholder: "Filter by employee", labelKey: "full_name" })}</Col>
                            <Col md={6}>{renderNativeSelect(performanceFilters.store, (value) => { setPerformanceFilters({ ...performanceFilters, store: value }); setPage("performanceReviews", 1); }, stores, "Filter by store")}</Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Table responsive bordered hover>
                    <thead><tr><th>Employee</th><th>Store</th><th>Period</th><th>Attendance</th><th>Productivity</th><th>Quality</th><th>Initiative</th><th>Late</th><th>Overall</th><th>Additional Info</th></tr></thead>
                    <tbody>
                        {pageRows.map((review) => {
                            const score = reviewScore(review);
                            const flag = performanceFlag(score);
                            return (
                                <tr key={review.id}>
                                    <td>{review.employee_name}</td>
                                    <td>{review.store_name}</td>
                                    <td>{review.period_start} to {review.period_end}</td>
                                    <td><ProgressBar now={percent(review.attendance_score)} label={`${review.attendance_score}%`} /></td>
                                    <td><ProgressBar now={percent(review.productivity_score)} label={`${review.productivity_score}%`} /></td>
                                    <td><ProgressBar now={percent(review.quality_score)} label={`${review.quality_score}%`} /></td>
                                    <td><ProgressBar now={percent(review.initiative_score)} label={`${review.initiative_score}%`} /></td>
                                    <td>{review.late_days}</td>
                                    <td><Badge bg={flag.variant}>{Math.round(score)} - {flag.label}</Badge></td>
                                    <td>{review.action || "-"}</td>
                                </tr>
                            );
                        })}
                        {!filteredPerformanceReviews.length && <tr><td colSpan={10} className="text-center text-muted">No reviews found.</td></tr>}
                    </tbody>
                </Table>
                {renderPagination("performanceReviews", totalPages, currentPage)}

                <Card className="mt-3">
                    <Card.Body>
                        <Card.Title>Employee Aggregate Performance</Card.Title>
                        <Table responsive bordered hover className="mb-0">
                            <thead><tr><th>Employee</th><th>Store</th><th>Reviews</th><th>Average Score</th><th>Flag</th></tr></thead>
                            <tbody>
                                {performanceAggregates.map((row) => (
                                    <tr key={row.employee.id}>
                                        <td>{row.employee.full_name}</td>
                                        <td>{row.employee.store_name}</td>
                                        <td>{row.count}</td>
                                        <td><ProgressBar now={percent(row.average)} label={`${Math.round(row.average)}%`} /></td>
                                        <td><Badge bg={row.flag.variant}>{row.flag.label}</Badge></td>
                                    </tr>
                                ))}
                                {!performanceAggregates.length && <tr><td colSpan={5} className="text-center text-muted">No aggregate performance yet.</td></tr>}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </>
        );
    };

    const filteredCompliance = useMemo(() => complianceDocuments.filter((doc) => {
        const employeeMatch = !complianceFilters.employee || String(doc.employee) === String(complianceFilters.employee);
        const storeMatch = !complianceFilters.store || employees.some((employee) => String(employee.id) === String(doc.employee) && String(employee.store) === String(complianceFilters.store));
        const documentMatch = !complianceFilters.document_type || doc.document_type === complianceFilters.document_type;
        const statusMatch = !complianceFilters.status || doc.status === complianceFilters.status;
        return employeeMatch && storeMatch && documentMatch && statusMatch;
    }), [complianceDocuments, complianceFilters, employees]);

    const renderCompliance = () => {
        const { currentPage, totalPages, pageRows } = getPageData("complianceDocuments", filteredCompliance);
        return (
            <>
                <Card className="mb-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Card.Title className="mb-0">{complianceEditId ? "Edit Compliance Document" : "Compliance Document"}</Card.Title>
                            {complianceEditId && (
                                <Button variant="outline-secondary" size="sm" onClick={() => {
                                    setComplianceEditId(null);
                                    setComplianceForm({ employee: "", document_type: "SHA", document_number: "", expiry_date: "", status: "VALID", notes: "" });
                                }}>
                                    Cancel Edit
                                </Button>
                            )}
                        </div>
                        <Row className="g-3">
                            <Col md={4}><Form.Label>Employee</Form.Label>{renderSearchableSelect({ value: complianceForm.employee, onChange: (value) => setComplianceForm({ ...complianceForm, employee: value }), options: employees, placeholder: "Search employee name", labelKey: "full_name" })}</Col>
                            <Col md={3}>
                                <Form.Label>Document</Form.Label>
                                <Form.Select value={complianceForm.document_type} onChange={(e) => setComplianceForm({ ...complianceForm, document_type: e.target.value })}>
                                    <option value="">Select document</option>
                                    <option value="CONTRACT">Contract</option>
                                    <option value="KRA_PIN">KRA PIN</option>
                                    <option value="SHA">SHA</option>
                                    <option value="NSSF">NSSF</option>
                                    <option value="ID">National ID</option>
                                    <option value="OTHER">Other</option>
                                </Form.Select>
                            </Col>
                            <Col md={3}><Form.Label>Reference number</Form.Label><Form.Control placeholder="Optional; employee master holds ID/KRA/SHA/NSSF" value={complianceForm.document_number} onChange={(e) => setComplianceForm({ ...complianceForm, document_number: e.target.value })} /></Col>
                            <Col md={2}><Form.Label>Expiry date</Form.Label><Form.Control type="date" value={complianceForm.expiry_date || ""} onChange={(e) => setComplianceForm({ ...complianceForm, expiry_date: e.target.value })} /></Col>
                            <Col md={3}>
                                <Form.Label>Status</Form.Label>
                                <Form.Select value={complianceForm.status} onChange={(e) => setComplianceForm({ ...complianceForm, status: e.target.value })}>
                                    <option value="">Select status</option>
                                    <option value="VALID">Valid</option>
                                    <option value="MISSING">Missing</option>
                                    <option value="EXPIRED">Expired</option>
                                    <option value="EXPIRING_SOON">Expiring Soon</option>
                                </Form.Select>
                            </Col>
                            <Col md={9}><Form.Label>Notes</Form.Label><Form.Control placeholder="Compliance notes" value={complianceForm.notes} onChange={(e) => setComplianceForm({ ...complianceForm, notes: e.target.value })} /></Col>
                            <Col md={12}><Button disabled={saving} onClick={() => submit(async () => {
                                const payload = { ...complianceForm, expiry_date: cleanNullable(complianceForm.expiry_date) };
                                if (complianceEditId) {
                                    await updateHRComplianceDocument(complianceEditId, payload);
                                } else {
                                    await createHRComplianceDocument(payload);
                                }
                                setComplianceEditId(null);
                                setComplianceForm({ employee: "", document_type: "SHA", document_number: "", expiry_date: "", status: "VALID", notes: "" });
                            }, complianceEditId ? "Compliance document updated." : "Compliance document saved.")}>{complianceEditId ? "Update Document" : "Save Document"}</Button></Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="mb-3">
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={3}>{renderSearchableSelect({ value: complianceFilters.employee, onChange: (value) => { setComplianceFilters({ ...complianceFilters, employee: value }); setPage("complianceDocuments", 1); }, options: employees, placeholder: "Filter by employee", labelKey: "full_name" })}</Col>
                            <Col md={3}>{renderNativeSelect(complianceFilters.store, (value) => { setComplianceFilters({ ...complianceFilters, store: value }); setPage("complianceDocuments", 1); }, stores, "Filter by store")}</Col>
                            <Col md={3}>
                                <Form.Select value={complianceFilters.document_type} onChange={(e) => { setComplianceFilters({ ...complianceFilters, document_type: e.target.value }); setPage("complianceDocuments", 1); }}>
                                    <option value="">Filter by document</option>
                                    <option value="CONTRACT">Contract</option>
                                    <option value="KRA_PIN">KRA PIN</option>
                                    <option value="SHA">SHA</option>
                                    <option value="NSSF">NSSF</option>
                                    <option value="ID">National ID</option>
                                    <option value="OTHER">Other</option>
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Select value={complianceFilters.status} onChange={(e) => { setComplianceFilters({ ...complianceFilters, status: e.target.value }); setPage("complianceDocuments", 1); }}>
                                    <option value="">Filter by status</option>
                                    <option value="VALID">Valid</option>
                                    <option value="MISSING">Missing</option>
                                    <option value="EXPIRED">Expired</option>
                                    <option value="EXPIRING_SOON">Expiring Soon</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Table responsive bordered hover>
                    <thead><tr><th>Employee</th><th>Store</th><th>Document</th><th>Reference</th><th>Expiry</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                        {pageRows.map((doc) => (
                            <tr key={doc.id}>
                                <td>{doc.employee_name}</td>
                                <td>{doc.store_name}</td>
                                <td>{doc.document_type}</td>
                                <td>{doc.document_number || "-"}</td>
                                <td>{doc.expiry_date || "-"}</td>
                                <td><Badge bg={doc.status === "VALID" ? "success" : "danger"}>{doc.status}</Badge></td>
                                <td><Button size="sm" variant="outline-primary" onClick={() => {
                                    setComplianceEditId(doc.id);
                                    setComplianceForm({ ...doc, expiry_date: doc.expiry_date || "" });
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}>Edit</Button></td>
                            </tr>
                        ))}
                        {!filteredCompliance.length && <tr><td colSpan={7} className="text-center text-muted">No compliance documents found.</td></tr>}
                    </tbody>
                </Table>
                {renderPagination("complianceDocuments", totalPages, currentPage)}
            </>
        );
    };

    const selectedOpeningApplicants = applicants.filter((applicant) => String(applicant.job_opening) === String(selectedJobOpening || applicantForm.job_opening));

    const prepareApplicantAsEmployee = async (applicant) => {
        if (!window.confirm(`Prepare ${applicant.full_name} for Employee Master?`)) return;
        const opening = jobOpenings.find((item) => String(item.id) === String(applicant.job_opening));
        const names = splitApplicantName(applicant.full_name);
        setEmployeeEditId(null);
        setEmployeeForm({
            ...blankEmployee,
            ...names,
            email: applicant.email || "",
            phone_number: applicant.phone_number || "",
            store: opening?.store || "",
            department: opening?.department || "",
            role: opening?.role || "",
        });
        setActiveModule("employees");
        setMessage({ type: "info", text: "Applicant details copied to Employee Master. Complete salary, documents, and hire date before saving." });
    };

    const renderRecruitment = () => {
        const { currentPage: openingPage, totalPages: openingPages, pageRows: openingRows } = getPageData("jobOpenings", jobOpenings);
        const { currentPage: applicantPage, totalPages: applicantPages, pageRows: applicantRows } = getPageData("applicants", selectedOpeningApplicants);
        return (
            <>
                <Row className="g-3 mb-3">
                    <Col lg={6}>
                        <Card>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Card.Title className="mb-0">{jobOpeningEditId ? "Edit Job Opening" : "Job Opening"}</Card.Title>
                                    {jobOpeningEditId && (
                                        <Button variant="outline-secondary" size="sm" onClick={() => {
                                            setJobOpeningEditId(null);
                                            setJobOpeningForm({ title: "", store: "", department: "", role: "", status: "OPEN", opened_at: "" });
                                        }}>
                                            Cancel Edit
                                        </Button>
                                    )}
                                </div>
                                <Row className="g-3">
                                    <Col md={12}><Form.Label>Opening title</Form.Label><Form.Control placeholder="Cashier, warehouse assistant, branch manager..." value={jobOpeningForm.title} onChange={(e) => setJobOpeningForm({ ...jobOpeningForm, title: e.target.value })} /></Col>
                                    <Col md={6}><Form.Label>Store</Form.Label>{renderNativeSelect(jobOpeningForm.store, (value) => setJobOpeningForm({ ...jobOpeningForm, store: value }), stores, "Select store")}</Col>
                                    <Col md={6}><Form.Label>Department</Form.Label>{renderNativeSelect(jobOpeningForm.department, (value) => setJobOpeningForm({ ...jobOpeningForm, department: value }), departments, "Select department")}</Col>
                                    <Col md={6}><Form.Label>Role</Form.Label>{renderNativeSelect(jobOpeningForm.role, (value) => setJobOpeningForm({ ...jobOpeningForm, role: value }), roles, "Select role", "title")}</Col>
                                    <Col md={3}>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select value={jobOpeningForm.status} onChange={(e) => setJobOpeningForm({ ...jobOpeningForm, status: e.target.value })}>
                                            <option value="OPEN">Open</option>
                                            <option value="INTERVIEW">Interview</option>
                                            <option value="ON_HOLD">On Hold</option>
                                            <option value="CLOSED">Closed</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}><Form.Label>Opened at</Form.Label><Form.Control type="date" value={jobOpeningForm.opened_at || ""} onChange={(e) => setJobOpeningForm({ ...jobOpeningForm, opened_at: e.target.value })} /></Col>
                                    <Col md={12}><Button disabled={saving} onClick={() => submit(async () => {
                                        const payload = { ...jobOpeningForm, role: cleanNullable(jobOpeningForm.role) };
                                        if (!payload.opened_at) delete payload.opened_at;
                                        if (jobOpeningEditId) {
                                            await updateHRJobOpening(jobOpeningEditId, payload);
                                        } else {
                                            await createHRJobOpening(payload);
                                        }
                                        setJobOpeningEditId(null);
                                        setJobOpeningForm({ title: "", store: "", department: "", role: "", status: "OPEN", opened_at: "" });
                                    }, jobOpeningEditId ? "Job opening updated." : "Job opening saved.")}>{jobOpeningEditId ? "Update Opening" : "Save Opening"}</Button></Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={6}>
                        <Card>
                            <Card.Body>
                                <Card.Title>Applicant</Card.Title>
                                <Row className="g-3">
                                    <Col md={12}><Form.Label>Job opening</Form.Label>{renderNativeSelect(applicantForm.job_opening, (value) => { setApplicantForm({ ...applicantForm, job_opening: value }); setSelectedJobOpening(value); }, jobOpenings, "Select job opening", (opening) => `${opening.title} - ${opening.store_name}`)}</Col>
                                    <Col md={6}><Form.Label>Applicant name</Form.Label><Form.Control placeholder="Full name" value={applicantForm.full_name} onChange={(e) => setApplicantForm({ ...applicantForm, full_name: e.target.value })} /></Col>
                                    <Col md={6}><Form.Label>Phone</Form.Label><Form.Control placeholder="Phone number" value={applicantForm.phone_number} onChange={(e) => setApplicantForm({ ...applicantForm, phone_number: e.target.value })} /></Col>
                                    <Col md={8}><Form.Label>Email</Form.Label><Form.Control type="email" placeholder="Email address" value={applicantForm.email} onChange={(e) => setApplicantForm({ ...applicantForm, email: e.target.value })} /></Col>
                                    <Col md={4}>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select value={applicantForm.status} onChange={(e) => setApplicantForm({ ...applicantForm, status: e.target.value })}>
                                            <option value="APPLIED">Applied</option>
                                            <option value="SHORTLISTED">Shortlisted</option>
                                            <option value="INTERVIEW">Interview</option>
                                            <option value="HIRED">Hired</option>
                                            <option value="REJECTED">Rejected</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={12}><Form.Label>Notes</Form.Label><Form.Control as="textarea" rows={3} placeholder="Interview notes or recruiter comments" value={applicantForm.notes} onChange={(e) => setApplicantForm({ ...applicantForm, notes: e.target.value })} /></Col>
                                    <Col md={12}><Button disabled={saving} onClick={() => submit(async () => {
                                        await createHRApplicant(applicantForm);
                                        setApplicantForm({ job_opening: applicantForm.job_opening, full_name: "", phone_number: "", email: "", status: "APPLIED", notes: "" });
                                    }, "Applicant saved.")}>Save Applicant</Button></Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Table responsive bordered hover>
                    <thead><tr><th>Opening</th><th>Store</th><th>Department</th><th>Role</th><th>Status</th><th>Applicants</th><th>Action</th></tr></thead>
                    <tbody>
                        {openingRows.map((opening) => (
                            <tr key={opening.id}>
                                <td>{opening.title}</td>
                                <td>{opening.store_name}</td>
                                <td>{opening.department_name}</td>
                                <td>{opening.role_title || "-"}</td>
                                <td>{opening.status}</td>
                                <td>{opening.applicant_count || 0}</td>
                                <td>
                                    <Button size="sm" variant="outline-dark" className="me-1" onClick={() => { setSelectedJobOpening(opening.id); setApplicantForm({ ...applicantForm, job_opening: opening.id }); setPage("applicants", 1); }}>View Applicants</Button>
                                    <Button size="sm" variant="outline-primary" onClick={() => {
                                        setJobOpeningEditId(opening.id);
                                        setJobOpeningForm({ ...opening, opened_at: opening.opened_at || "" });
                                    }}>Edit</Button>
                                </td>
                            </tr>
                        ))}
                        {!jobOpenings.length && <tr><td colSpan={7} className="text-center text-muted">No job openings yet.</td></tr>}
                    </tbody>
                </Table>
                {renderPagination("jobOpenings", openingPages, openingPage)}

                <Card className="my-3">
                    <Card.Body>
                        <Form.Label>Applicants list</Form.Label>
                        {renderNativeSelect(selectedJobOpening, (value) => { setSelectedJobOpening(value); setApplicantForm({ ...applicantForm, job_opening: value }); setPage("applicants", 1); }, jobOpenings, "Select opening to list applicants", (opening) => `${opening.title} - ${opening.store_name}`)}
                    </Card.Body>
                </Card>
                <Table responsive bordered hover>
                    <thead><tr><th>Applicant</th><th>Opening</th><th>Store</th><th>Phone</th><th>Email</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                        {applicantRows.map((applicant) => (
                            <tr key={applicant.id}>
                                <td>{applicant.full_name}</td>
                                <td>{applicant.job_title}</td>
                                <td>{applicant.store_name}</td>
                                <td>{applicant.phone_number || "-"}</td>
                                <td>{applicant.email || "-"}</td>
                                <td>{applicant.status}</td>
                                <td>
                                    {isAdmin && ["SHORTLISTED", "INTERVIEW"].includes(applicant.status) && (
                                        <Button size="sm" variant="outline-success" className="me-1" onClick={() => prepareApplicantAsEmployee(applicant)}>Add to Employee Master</Button>
                                    )}
                                    <Button size="sm" variant="outline-danger" disabled={saving || applicant.status === "REJECTED"} onClick={() => {
                                        if (!window.confirm(`Reject ${applicant.full_name}?`)) return;
                                        submit(async () => updateHRApplicant(applicant.id, { status: "REJECTED" }), "Applicant rejected.");
                                    }}>Reject</Button>
                                </td>
                            </tr>
                        ))}
                        {!selectedOpeningApplicants.length && <tr><td colSpan={7} className="text-center text-muted">Select a job opening to view applicants.</td></tr>}
                    </tbody>
                </Table>
                {renderPagination("applicants", applicantPages, applicantPage)}
            </>
        );
    };

    const renderAnalytics = () => {
        const payrollByStore = (dashboard.payroll_by_store || []).filter((row) => !analyticsStoreFilter || String(row.store_id) === String(analyticsStoreFilter));
        const maxPayroll = Math.max(1, ...payrollByStore.map((row) => Number(row.total || 0)));
        const storePerformanceRows = stores
            .map((store) => {
                const storeEmployees = employees.filter((employee) => String(employee.store) === String(store.id));
                const storeReviews = performanceReviews.filter((review) => storeEmployees.some((employee) => String(employee.id) === String(review.employee)));
                if (!storeReviews.length) return null;
                const score = storeReviews.reduce((total, review) => total + reviewScore(review), 0) / storeReviews.length;
                const target = Number(storePerformanceTargets[store.id] || 85);
                const variance = score - target;
                return { store, score, target, variance, reviewCount: storeReviews.length };
            })
            .filter(Boolean)
            .filter((row) => !analyticsStoreFilter || String(row.store.id) === String(analyticsStoreFilter));

        const aggregatePerformance = storePerformanceRows.length
            ? storePerformanceRows.reduce((total, row) => total + row.score, 0) / storePerformanceRows.length
            : 0;
        return (
            <>
                <Card className="mb-3">
                    <Card.Body>
                        <Row className="g-3 align-items-end">
                            <Col md={6}>
                                <Form.Label>Store view</Form.Label>
                                {renderNativeSelect(analyticsStoreFilter, (value) => setAnalyticsStoreFilter(value), stores, "All stores combined")}
                            </Col>
                            <Col md={6}>
                                <div className="text-muted small">Warehouse/account users can keep this as all stores combined, or select one branch for a per-store view.</div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
                <Row className="g-3 mb-3">
                    <Col md={3}><Card><Card.Body><Card.Title>Absentee Records</Card.Title><h2>{dashboard.absentee_records || 0}</h2></Card.Body></Card></Col>
                    <Col md={3}><Card><Card.Body><Card.Title>Overtime Cost</Card.Title><h2>{currency(dashboard.overtime_cost)}</h2></Card.Body></Card></Col>
                    <Col md={3}><Card><Card.Body><Card.Title>Compliance Gaps</Card.Title><h2>{dashboard.compliance_gaps || 0}</h2></Card.Body></Card></Col>
                    <Col md={3}><Card><Card.Body><Card.Title>Performance Aggregate</Card.Title><h2>{Math.round(aggregatePerformance)}%</h2></Card.Body></Card></Col>
                </Row>
                <Table responsive bordered hover>
                    <thead><tr><th>Store</th><th>Payroll Cost</th><th>Cost Share</th></tr></thead>
                    <tbody>
                        {payrollByStore.map((row) => (
                            <tr key={row.store_id || row.store_name}>
                                <td>{row.store_name}</td>
                                <td>{currency(row.total)}</td>
                                <td><ProgressBar now={(Number(row.total || 0) / maxPayroll) * 100} /></td>
                            </tr>
                        ))}
                        {!payrollByStore.length && <tr><td colSpan={3} className="text-center text-muted">No payroll by store yet.</td></tr>}
                    </tbody>
                </Table>
                <Card className="mt-3">
                    <Card.Body>
                        <Card.Title>Store Performance Against Target</Card.Title>
                        <Table responsive bordered hover className="mb-0">
                            <thead><tr><th>Store</th><th>Target</th><th>Actual</th><th>Variance</th><th>Status</th><th>Reviews</th></tr></thead>
                            <tbody>
                                {storePerformanceRows.map((row) => {
                                    const variant = row.variance >= 5 ? "success" : row.variance >= 0 ? "primary" : row.variance <= -10 ? "danger" : "warning";
                                    const label = row.variance >= 5 ? "Overachieved" : row.variance >= 0 ? "Achieved" : row.variance <= -10 ? "Underachieved" : "Slightly under";
                                    return (
                                        <tr key={row.store.id}>
                                            <td>{row.store.name}</td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={row.target}
                                                    onChange={(e) => setStorePerformanceTargets((current) => ({ ...current, [row.store.id]: e.target.value }))}
                                                />
                                            </td>
                                            <td><ProgressBar now={percent(row.score)} label={`${Math.round(row.score)}%`} /></td>
                                            <td>{row.variance >= 0 ? "+" : ""}{row.variance.toFixed(1)}%</td>
                                            <td><Badge bg={variant}>{label}</Badge></td>
                                            <td>{row.reviewCount}</td>
                                        </tr>
                                    );
                                })}
                                {!storePerformanceRows.length && <tr><td colSpan={6} className="text-center text-muted">No performance reviews available for this store view.</td></tr>}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </>
        );
    };

    const employeeUseCounts = useMemo(() => ({
        departments: departments.reduce((map, department) => ({ ...map, [department.id]: employees.filter((employee) => String(employee.department) === String(department.id)).length }), {}),
        roles: roles.reduce((map, role) => ({ ...map, [role.id]: employees.filter((employee) => String(employee.role) === String(role.id)).length }), {}),
        salaries: salaryStructures.reduce((map, salary) => ({ ...map, [salary.id]: employees.filter((employee) => String(employee.salary_structure) === String(salary.id)).length }), {}),
    }), [departments, employees, roles, salaryStructures]);

    const renderSettings = () => (
        <Row className="g-3">
            {!isAdmin && <Col xs={12}><Alert variant="warning">Only admins can edit or delete HR settings.</Alert></Col>}
            <Col lg={4}>
                <Card>
                    <Card.Body>
                        <Card.Title>Departments</Card.Title>
                        <div className="d-grid gap-2 mb-3">
                            <Form.Control placeholder="Department name" value={departmentForm.name} disabled={!isAdmin} onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })} />
                            <Form.Control placeholder="Department description" value={departmentForm.description} disabled={!isAdmin} onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })} />
                            <Button disabled={!isAdmin || saving} onClick={() => submit(async () => {
                                if (departmentForm.id) {
                                    await updateHRDepartment(departmentForm.id, departmentForm);
                                } else {
                                    await createHRDepartment(departmentForm);
                                }
                                setDepartmentForm({ id: null, name: "", description: "", is_active: true });
                            }, departmentForm.id ? "Department updated." : "Department saved.")}>{departmentForm.id ? "Update Department" : "Save Department"}</Button>
                        </div>
                        <Table responsive size="sm">
                            <tbody>
                                {departments.map((department) => {
                                    const used = employeeUseCounts.departments[department.id] || 0;
                                    const hasRoles = roles.some((role) => String(role.department) === String(department.id));
                                    return (
                                        <tr key={department.id}>
                                            <td>{department.name}<div className="text-muted small">{used} employees</div></td>
                                            <td className="text-end">
                                                {isAdmin && <Button size="sm" variant="outline-primary" className="me-1" onClick={() => setDepartmentForm(department)}>Edit</Button>}
                                                {isAdmin && <Button size="sm" variant="outline-danger" disabled={used > 0 || hasRoles || saving} onClick={() => submit(async () => deleteHRDepartment(department.id), "Department deleted.")}>Delete</Button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
            <Col lg={4}>
                <Card>
                    <Card.Body>
                        <Card.Title>Roles</Card.Title>
                        <div className="d-grid gap-2 mb-3">
                            {renderNativeSelect(roleForm.department, (value) => setRoleForm({ ...roleForm, department: value }), departments, "Select department")}
                            <Form.Control placeholder="Role title" value={roleForm.title} disabled={!isAdmin} onChange={(e) => setRoleForm({ ...roleForm, title: e.target.value })} />
                            <Form.Control placeholder="Role description" value={roleForm.description} disabled={!isAdmin} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} />
                            <Button disabled={!isAdmin || saving} onClick={() => submit(async () => {
                                if (roleForm.id) {
                                    await updateHRRole(roleForm.id, roleForm);
                                } else {
                                    await createHRRole(roleForm);
                                }
                                setRoleForm({ id: null, department: "", title: "", description: "", is_active: true });
                            }, roleForm.id ? "Role updated." : "Role saved.")}>{roleForm.id ? "Update Role" : "Save Role"}</Button>
                        </div>
                        <Table responsive size="sm">
                            <tbody>
                                {roles.map((role) => {
                                    const used = employeeUseCounts.roles[role.id] || 0;
                                    return (
                                        <tr key={role.id}>
                                            <td>{role.title}<div className="text-muted small">{role.department_name} / {used} employees</div></td>
                                            <td className="text-end">
                                                {isAdmin && <Button size="sm" variant="outline-primary" className="me-1" onClick={() => setRoleForm(role)}>Edit</Button>}
                                                {isAdmin && <Button size="sm" variant="outline-danger" disabled={used > 0 || saving} onClick={() => submit(async () => deleteHRRole(role.id), "Role deleted.")}>Delete</Button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
            <Col lg={4}>
                <Card>
                    <Card.Body>
                        <Card.Title>Salary Structures</Card.Title>
                        <div className="d-grid gap-2 mb-3">
                            <Form.Control placeholder="Structure name" value={salaryForm.name} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, name: e.target.value })} />
                            <Form.Select value={salaryForm.employment_type} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, employment_type: e.target.value })}>
                                <option value="FULL_TIME">Full-time</option>
                                <option value="CASUAL">Casual</option>
                                <option value="CONTRACT">Contract</option>
                            </Form.Select>
                            <Form.Select value={salaryForm.pay_frequency} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, pay_frequency: e.target.value })}>
                                <option value="DAILY">Daily</option>
                                <option value="WEEKLY">Weekly</option>
                                <option value="MONTHLY">Monthly</option>
                            </Form.Select>
                            <Form.Control type="number" placeholder="Basic salary" value={salaryForm.basic_salary} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, basic_salary: e.target.value })} />
                            <Form.Control type="number" placeholder="Daily rate for casuals" value={salaryForm.daily_rate} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, daily_rate: e.target.value })} />
                            <Form.Control type="number" placeholder="Housing allowance" value={salaryForm.housing_allowance} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, housing_allowance: e.target.value })} />
                            <Form.Control type="number" placeholder="Transport allowance" value={salaryForm.transport_allowance} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, transport_allowance: e.target.value })} />
                            <Form.Control type="number" placeholder="Other allowances" value={salaryForm.other_allowances} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, other_allowances: e.target.value })} />
                            <Form.Control type="number" placeholder="Overtime hourly rate" value={salaryForm.overtime_hourly_rate} disabled={!isAdmin} onChange={(e) => setSalaryForm({ ...salaryForm, overtime_hourly_rate: e.target.value })} />
                            <Button disabled={!isAdmin || saving} onClick={() => submit(async () => {
                                if (salaryEditId) {
                                    await updateHRSalaryStructure(salaryEditId, salaryPayload(salaryForm));
                                } else {
                                    await createHRSalaryStructure(salaryPayload(salaryForm));
                                }
                                setSalaryEditId(null);
                                setSalaryForm(blankSalary);
                            }, salaryEditId ? "Salary structure updated." : "Salary structure saved.")}>{salaryEditId ? "Update Structure" : "Save Structure"}</Button>
                        </div>
                        <Table responsive size="sm">
                            <tbody>
                                {salaryStructures.map((salary) => {
                                    const used = employeeUseCounts.salaries[salary.id] || 0;
                                    return (
                                        <tr key={salary.id}>
                                            <td>{salary.name}<div className="text-muted small">{salary.pay_frequency} / {used} employees</div></td>
                                            <td className="text-end">
                                                {isAdmin && <Button size="sm" variant="outline-primary" className="me-1" onClick={() => {
                                                    setSalaryEditId(salary.id);
                                                    setSalaryForm({ ...blankSalary, ...salary });
                                                }}>Edit</Button>}
                                                {isAdmin && <Button size="sm" variant="outline-danger" disabled={used > 0 || saving} onClick={() => submit(async () => deleteHRSalaryStructure(salary.id), "Salary structure deleted.")}>Delete</Button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );

    const renderActiveModule = () => {
        const renderers = {
            employees: renderEmployees,
            attendance: renderAttendance,
            leave: renderLeave,
            payroll: renderPayroll,
            performance: renderPerformance,
            compliance: renderCompliance,
            recruitment: renderRecruitment,
            analytics: renderAnalytics,
            settings: renderSettings,
        };
        return renderers[activeModule]();
    };

    return (
        <Container fluid className="mt-4 hr-page text-start">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1 className="mb-0">Human Resources</h1>
                </Col>
                <Col xs="auto">
                    <Button variant="outline-secondary" onClick={loadHR} disabled={loading || saving}>Refresh</Button>
                </Col>
            </Row>

            {message && <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>{message.text}</Alert>}

            <Row className="g-3 mb-3">
                <Col md={3}><Card><Card.Body><Card.Title>Active Staff</Card.Title><h2>{dashboard.active_staff || 0}</h2></Card.Body></Card></Col>
                <Col md={3}><Card><Card.Body><Card.Title>Monthly Payroll</Card.Title><h2>{currency(dashboard.monthly_payroll)}</h2></Card.Body></Card></Col>
                <Col md={3}><Card><Card.Body><Card.Title>Compliance Gaps</Card.Title><h2>{dashboard.compliance_gaps || 0}</h2></Card.Body></Card></Col>
                <Col md={3}><Card><Card.Body><Card.Title>Pending Approvals</Card.Title><h2>{dashboard.pending_leave || 0}</h2></Card.Body></Card></Col>
            </Row>

            <Row className="g-3">
                <Col lg={3}>
                    <Card>
                        <Card.Body>
                            <Card.Title>HR Modules</Card.Title>
                            <div className="d-grid gap-2">
                                {modules.map((module) => (
                                    <Button
                                        key={module.key}
                                        variant={activeModule === module.key ? "dark" : "outline-dark"}
                                        className="text-start"
                                        onClick={() => setActiveModule(module.key)}
                                    >
                                        {module.label}
                                    </Button>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={9}>
                    <div className="hr-workspace-panel">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="mb-0">{modules.find((module) => module.key === activeModule)?.label}</h4>
                            <Badge bg="secondary">Live API</Badge>
                        </div>
                        {loading ? <div className="text-center py-5"><Spinner animation="border" /></div> : renderActiveModule()}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default HR;